/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { colors } from '@blend/consumer-components';
import { useHistory, useLocation } from 'react-router';
import { NavigationSidebar } from '../../../components/common/NavigationSidebar/NavigationSidebar';
import { notaryAPI } from '../../../api/notary';
import { NotaryAPI } from '../../../api/notary/types';
import { pageSize } from '../../../components/common/Pipeline/constants';
import { RouterStates } from '../../../definitions/RouterStates';
import { usePermissions } from '../../../hooks/usePermissions';
import { defaultPipelineFilters } from '../../../../src/utils/filters';
import { RoomsPipeline } from './RoomsPipeline';
import { convertURLSearchToFilterParams, updateUrlSearch } from '@utils/filters';
import { FilterParams } from '@shared/types/pipeline';

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    backgroundColor: colors.snowPlus1,
  },
  pipeline: {
    width: '100%',
    height: '100%',
    overflow: 'scroll',
  },
});

export const Rooms: React.FC = () => {
  const hasViewRoomListPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRViewRoomList);
  const hasSuperAdminPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);
  const [isLoading, setIsLoading] = useState(true);
  const [rooms, setRooms] = useState<NotaryAPI.RoomListItem[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setNewPage] = useState<number>(defaultPipelineFilters(hasSuperAdminPermission).page);
  const history = useHistory();
  const location = useLocation();
  const search = location.search.slice(1);
  const fetchRooms = async (filter: FilterParams) => {
    if (!hasViewRoomListPermission) {
      history.push(RouterStates.ROOM_ASSIGNMENTS);
      return;
    }
    try {
      setIsLoading(true);
      const result = await notaryAPI.rooms.fetchRooms(pageSize, filter.page * pageSize, filter);
      setRooms(result.rooms);
      setTotalCount(result.totalCount);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const setPage = (page: number) => {
    setNewPage(page);
    const params = new URLSearchParams(location.search.slice(1));
    params.set('page', page.toString());
    history.push(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    (async () => {
      const url = new URL(location.pathname, window.location.origin);
      let urlFilters = defaultPipelineFilters(hasSuperAdminPermission);

      if (location.search.length > 0) {
        urlFilters = convertURLSearchToFilterParams(location.search.slice(1), hasSuperAdminPermission);
      } else {
        updateUrlSearch(url, urlFilters);

        const pathname = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
        history.replace(`${pathname}?${url.searchParams.toString()}`);
        return;
      }

      updateUrlSearch(url, urlFilters);
      setNewPage(() => urlFilters.page);
      // if location.pathname ends with / then remove it
      const pathname = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
      history.replace(`${pathname}?${url.searchParams.toString()}`);
      await fetchRooms(urlFilters);
    })();
  }, [search]);

  return (
    <div className={css(styles.wrapper)}>
      <NavigationSidebar />
      <div className={css(styles.pipeline)}>
        <RoomsPipeline
          data-it="RoomsPipeline"
          isLoading={isLoading}
          rooms={rooms}
          hasError={hasError}
          page={page}
          totalCount={totalCount}
          fetchRooms={fetchRooms}
          setPage={setPage}
        />
      </div>
    </div>
  );
};
